import { Controller } from "@hotwired/stimulus"
import { savePlan, serviceName } from "plan_storage"

// Current Services — "Recommended from Goals & Barriers" picker.
// Mirrors the legacy editor (pcp_builder.html#5992) — RC + Generic
// resources referenced in plan.goals[].resources and
// plan.barriers[].resources surface as add-once chips grouped by
// service type. Clicking + Add pushes a linked row onto
// plan.currentServices (the same `{ id, text, linkedType }` shape
// the legacy uses) so the cross-reference badges + downstream
// renderers continue to recognise it as a link.

const STORAGE_KEY = "pcp_plan_data"

// ── Schedule helpers — mirror pcp_builder.html#1660-1724. periodMap
// gives the per-year multiplier for each dayType (e.g. Weekly → 52,
// Mon-Fri → 181 school days). periodLabel feeds the "based on …"
// caption under the annual-hours badge. fmtSchedUnit returns the
// "/wk" / "/mo" / "/yr" suffix.
const PERIOD_MAP = {
  "Mon-Fri": 181, "Sat-Sun": 104, "Non-School": 80, "Daily": 365,
  "Sun": 52, "Mon": 52, "Tue": 52, "Wed": 52, "Thu": 52, "Fri": 52, "Sat": 52,
  "Mon-Wed-Fri": 156, "Tue-Thu": 104, "Mon-Wed": 104, "Tue-Thu-Sat": 156,
  "Weekly": 52, "Bi-Weekly": 26, "Monthly": 12, "Quarterly": 4,
  "Semi-Annually": 2, "Annually": 1, "As Needed": 0
}
const PERIOD_LABEL = {
  "Mon-Fri": "181 school days", "Sat-Sun": "104 weekends", "Non-School": "80 non-school days",
  "Daily": "365 days", "Sun": "52 Sundays", "Mon": "52 Mondays", "Tue": "52 Tuesdays",
  "Wed": "52 Wednesdays", "Thu": "52 Thursdays", "Fri": "52 Fridays", "Sat": "52 Saturdays",
  "Mon-Wed-Fri": "156 Mon/Wed/Fri days", "Tue-Thu": "104 Tue/Thu days",
  "Mon-Wed": "104 Mon/Wed days", "Tue-Thu-Sat": "156 Tue/Thu/Sat days",
  "Weekly": "52 weeks", "Bi-Weekly": "26 bi-weeks", "Monthly": "12 months",
  "Quarterly": "4 quarters", "Semi-Annually": "2 halves", "Annually": "1 year"
}
function fmtAnnual(totalHrs) {
  const h = Math.floor(totalHrs)
  const m = Math.round((totalHrs - h) * 60)
  if (m === 0) return `${h.toLocaleString()} hrs`
  if (h === 0) return `${m} min`
  return `${h.toLocaleString()} hrs ${m} min`
}
export default class extends Controller {
  static targets = ["list", "empty"]

  connect() {
    this.plan = this._load()
    this._autoPopulate()
    this._render()
    // Re-render whenever goals / barriers / currentServices change so
    // a suggestion that just got added disappears, and a freshly-added
    // goal resource surfaces here without a refresh.
    this._onChange = (e) => {
      const p = e.detail?.path || ""
      if (/^(goals|barriers|currentServices)(\.|$)/.test(p) || p === "*") {
        // Reuse the already-parsed plan from the event instead of a fresh
        // JSON.parse of the whole plan on every change.
        this.plan = e.detail.plan || this._load()
        this._autoPopulate()
        this._render()
      }
    }
    document.addEventListener("plan:change", this._onChange)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onChange)
  }

  // Click + Add → push a single linked row for `type` (idempotent;
  // the heuristic in _isAdded filters this type out of the list
  // after the next render, so the button doesn't appear twice).
  add(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.csType
    if (!type) return
    // Manually adding a previously-dismissed service un-dismisses it so it's
    // treated like any other linked row from here on.
    if (this.plan.meta && Array.isArray(this.plan.meta.csDismissedLinks)) {
      this.plan.meta.csDismissedLinks = this.plan.meta.csDismissedLinks.filter(t => t !== type)
    }
    if (!Array.isArray(this.plan.currentServices)) this.plan.currentServices = []
    // Seed the funding tag from the first matching source resource so
    // the row shows RC / Generic correctly the moment it's added.
    // dynamic_list_controller#render also re-resolves on every render
    // so later source flips propagate without a stale snapshot.
    const src = this._firstSource(type)
    this.plan.currentServices.push({
      id: Date.now() + Math.random(),
      text: type,
      linkedType: type,
      tag: src?.tag === "rc" ? "rc" : "general"
    })
    this._save()
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "currentServices", plan: this.plan }
    }))
    this._render()
  }

  // Auto-add a linked Current Services row for every RC/Generic resource in
  // Goals/Barriers, so they populate the Service List without a manual + Add.
  // Skips anything the user dismissed (removed / cleared) so it doesn't bounce
  // back — those still appear in this panel to re-add deliberately. Idempotent:
  // once added they're excluded by _collectGrouped, so the plan:change this
  // dispatches doesn't loop.
  _autoPopulate() {
    const grouped = this._collectGrouped()
    if (grouped.size === 0) return
    const dismissed = new Set((this.plan.meta && this.plan.meta.csDismissedLinks) || [])
    if (!Array.isArray(this.plan.currentServices)) this.plan.currentServices = []
    let added = false
    grouped.forEach((items, type) => {
      if (dismissed.has(type)) return
      const tag = items.some(r => r.tag === "rc") ? "rc" : "general"
      this.plan.currentServices.push({ id: Date.now() + Math.random(), text: type, linkedType: type, tag })
      added = true
    })
    if (!added) return
    this._save()
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "currentServices", plan: this.plan }
    }))
  }

  _firstSource(type) {
    const t = String(type || "").trim()
    if (!t) return null
    const fromArr = (arr) => (arr || []).flatMap(g => g.resources || [])
      .find(r => r && r.tag !== "unmet" && serviceName(r) === t)
    return fromArr(this.plan.goals) || fromArr(this.plan.barriers) || null
  }

  // -----------------------------------------------------------------
  _render() {
    if (!this.hasListTarget) return
    const grouped = this._collectGrouped()
    const incomplete = this._collectIncomplete()
    if (grouped.size === 0 && incomplete.length === 0) {
      if (this.hasEmptyTarget) this.emptyTarget.hidden = false
      this.listTarget.innerHTML = ""
      return
    }
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    this.listTarget.innerHTML = (incomplete.length ? this._warnHtml(incomplete) : "") +
      [...grouped.entries()].map(([type, items]) => `
      <div class="cs-sugg-card">
        <div class="cs-sugg-head">
          <div class="cs-sugg-head-left">
            <span class="cs-sugg-type">${this._esc(type)}</span>
            <span class="cs-sugg-count">${items.length}</span>
          </div>
          <button type="button"
                  class="cs-sugg-add"
                  data-action="cs-suggestions#add"
                  data-cs-type="${this._esc(type)}">+ Add</button>
        </div>
        <div class="cs-sugg-rows">
          ${items.map(r => `
            <div class="cs-sugg-row">
              <span class="cs-sugg-tag cs-sugg-tag-${this._esc(r.tag)}">${r.tag === "rc" ? "RC" : "Generic"}</span>
              <span class="cs-sugg-comment">${this._esc(r.comment || "")}</span>
              <a href="${this._esc(this._refHref(r))}"
                 class="cs-sugg-ref cs-sugg-ref-link cs-sugg-ref-${this._esc(r.source)}"
                 title="Open ${this._esc(r.sourceRef)} in the editor">${this._esc(r.sourceRef)} <span class="cs-sugg-ref-ar">&rarr;</span></a>
            </div>`).join("")}
        </div>
      </div>`).join("")
  }

  _collectGrouped() {
    const cs = this.plan.currentServices || []
    const existingTexts       = cs.map(s => (s.text || "").trim().toLowerCase()).filter(Boolean)
    const existingLinkedTypes = new Set(cs.filter(s => s.linkedType).map(s => s.linkedType))
    // Hide a suggestion only when the SAME service is already in the
    // list — an exact linkedType match or an exact (case-insensitive)
    // text match. The old heuristic also matched on substrings, which
    // wrongly suppressed unrelated RC/Generic resources once any
    // service was added (e.g. adding "Support" hid every type that
    // contained — or was contained by — "support"). Exact-match keeps
    // the promise that every RC/Generic resource surfaces here.
    const isAdded = (typ) => {
      if (typ && existingLinkedTypes.has(typ)) return true
      const lower = (typ || "").trim().toLowerCase()
      if (!lower) return true
      return existingTexts.includes(lower)
    }
    const out = []
    ;(this.plan.goals || []).forEach((g, gi) => {
      const ref = `Goal ${gi + 1}`
      ;(g.resources || []).forEach(r => {
        if (r.tag === "unmet") return
        const rType = serviceName(r)
        if (!rType || isAdded(rType)) return
        out.push({ type: rType, tag: r.tag || "general", comment: r.comment || "", sourceRef: ref, source: "goal", sourceIndex: gi })
      })
    })
    ;(this.plan.barriers || []).forEach((b, bi) => {
      const ref = `Barrier ${bi + 1}`
      ;(b.resources || []).forEach(r => {
        if (r.tag === "unmet") return
        const rType = serviceName(r)
        if (!rType || isAdded(rType)) return
        out.push({ type: rType, tag: r.tag || "general", comment: r.comment || "", sourceRef: ref, source: "barrier", sourceIndex: bi })
      })
    })
    const grouped = new Map()
    out.forEach(r => {
      if (!grouped.has(r.type)) grouped.set(r.type, [])
      grouped.get(r.type).push(r)
    })
    return grouped
  }

  // RC/Generic resources that carry REAL content (a schedule, cost, comment
  // or vendor) but have NO Service selected. A Current Service needs a name,
  // so these can't be auto-listed — but silently dropping them looks like
  // "my RC service isn't showing". Instead we surface a warning that
  // deep-links to the goal so the user picks a Service and it appears.
  // Empty stubs (a resource just added, nothing filled in) are ignored so
  // the warning only fires on genuinely-incomplete entries.
  _collectIncomplete() {
    const hasContent = (r) => {
      if (String(r.comment || "").trim()) return true
      if (String(r.vendorName || "").trim()) return true
      if (Array.isArray(r.vendors) && r.vendors.length) return true
      if (String(r.cost || "").trim()) return true
      // A schedule row only appears after a deliberate "+ Add Frequency and
      // Schedule" click (fresh resources start with schedule: []), so its
      // mere presence signals the user started configuring a real service —
      // even if the row's own fields are still blank.
      if (Array.isArray(r.schedule) && r.schedule.length) return true
      return false
    }
    const out = []
    const scan = (arr, label, source) => (arr || []).forEach((g, i) => {
      ;(g.resources || []).forEach(r => {
        if (!r || r.tag === "unmet") return
        if (serviceName(r)) return       // named → handled by _collectGrouped
        if (!hasContent(r)) return       // untouched stub → don't nag
        out.push({ sourceRef: `${label} ${i + 1}`, source, sourceIndex: i, tag: r.tag || "general" })
      })
    })
    scan(this.plan.goals, "Goal", "goal")
    scan(this.plan.barriers, "Barrier", "barrier")
    return out
  }

  _warnHtml(list) {
    const n = list.length
    const refs = list.map(r => `
      <a href="${this._esc(this._refHref(r))}"
         class="cs-sugg-ref cs-sugg-ref-link cs-sugg-ref-${this._esc(r.source)}"
         title="Open ${this._esc(r.sourceRef)} in the editor">${this._esc(r.sourceRef)} <span class="cs-sugg-ref-ar">&rarr;</span></a>`).join("")
    return `
      <div class="cs-sugg-warn">
        <div class="cs-sugg-warn-head">
          <span class="cs-sugg-warn-icon">&#9888;</span>
          <span>${n} RC/Generic item${n === 1 ? "" : "s"} can't be listed yet &mdash; no Service is selected.</span>
        </div>
        <div class="cs-sugg-warn-body">Open the goal and choose a Service so it appears in Current Services:</div>
        <div class="cs-sugg-warn-refs">${refs}</div>
      </div>`
  }

  // Deep-link a suggestion back to its source card in the editor.
  // Goals carry the preview's ref-goal anchor so dynamic_list's
  // _scrollToHashGoal scrolls straight to that card; barriers just
  // open their section (no per-barrier anchor exists yet).
  _refHref(r) {
    if (r.source === "goal" && Number.isInteger(r.sourceIndex)) {
      return `/editor/native/goals#ref-goal-${r.sourceIndex}`
    }
    if (r.source === "barrier") return `/editor/native/barriers`
    return `/editor/native/goals`
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
  _load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") }
    catch (_) { return {} }
  }
  _save() {
    savePlan(this.plan)
  }
}
